/**
 * Widget Configuration Types
 *
 * Defines the configuration interface that host pages use to customize
 * the tax calculator widget. Config values are read from:
 *   1. data-* attributes on the #tax-calculator container element
 *   2. data-* attributes on the embed <script> tag
 *   3. The config object passed to TaxCalculator.mount()
 *
 * Priority (highest wins): mount() argument > container attributes > script tag attributes
 */

export interface WidgetConfig {
  /** Visual theme. Currently supports 'light'. Extensible for future themes. */
  theme: string;

  /** BCP-47 locale tag for number/date formatting (e.g. 'es-ES', 'en-US'). */
  locale: string;

  /** Absolute base URL for the tax-calculator API (e.g. 'https://api.example.com/api/v1'). */
  apiBaseUrl: string;

  /** Pre-selected country code: 'ES' | 'US' | null (user picks inside the flow). */
  country: string | null;

  /** Tax year (default 2024). */
  taxYear: number;

  /** Pre-selected region for Spain (e.g. 'Andalusia'). */
  region?: string;

  /** Callback fired when the questionnaire flow completes. */
  onComplete?: (summary: unknown) => void;

  /** Arbitrary key-value pairs the host may pass through to the flow. */
  extras?: Record<string, string>;
}

export const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  theme: 'light',
  locale: 'en',
  apiBaseUrl: '',
  country: null,
  taxYear: 2024,
};

/**
 * Parse widget configuration from a DOM element's data-* attributes.
 *
 * Attribute mapping (all optional):
 *   data-theme        → theme
 *   data-locale       → locale
 *   data-api-base-url → apiBaseUrl
 *   data-country      → country
 *   data-tax-year     → taxYear
 *   data-region       → region
 *
 * Any data-* attribute not in the list above is collected into `extras`.
 */
export function parseConfigFromElement(el: HTMLElement): Partial<WidgetConfig> {
  const d = el.dataset;
  const config: Partial<WidgetConfig> & { extras?: Record<string, string> } = {};

  if (d.theme) config.theme = d.theme;
  if (d.locale) config.locale = d.locale;
  if (d.apiBaseUrl) config.apiBaseUrl = d.apiBaseUrl;
  if (d.country) config.country = d.country;
  if (d.taxYear) config.taxYear = parseInt(d.taxYear, 10);
  if (d.region) config.region = d.region;

  const knownKeys = new Set([
    'theme', 'locale', 'apiBaseUrl', 'country', 'taxYear', 'region',
    'baseUrl', 'autoMount',
  ]);
  const extras: Record<string, string> = {};
  for (const [key, value] of Object.entries(d)) {
    if (!knownKeys.has(key) && value !== undefined) {
      extras[key] = value;
    }
  }
  if (Object.keys(extras).length > 0) config.extras = extras;

  return config;
}

/**
 * Merge partial configs (later values override earlier).
 */
export function mergeConfig(...partials: Partial<WidgetConfig>[]): WidgetConfig {
  const merged = { ...DEFAULT_WIDGET_CONFIG };
  for (const p of partials) {
    if (p.theme !== undefined) merged.theme = p.theme;
    if (p.locale !== undefined) merged.locale = p.locale;
    if (p.apiBaseUrl !== undefined) merged.apiBaseUrl = p.apiBaseUrl;
    if (p.country !== undefined) merged.country = p.country;
    if (p.taxYear !== undefined) merged.taxYear = p.taxYear;
    if (p.region !== undefined) merged.region = p.region;
    if (p.onComplete !== undefined) merged.onComplete = p.onComplete;
    if (p.extras !== undefined) {
      merged.extras = { ...merged.extras, ...p.extras };
    }
  }
  return merged;
}
